import {
  CreateScimConfigurationDto,
  ScimConfigurationDto,
  scimService,
  ScimSyncLogDto,
  ScimSyncRequestDto,
  UpdateScimConfigurationDto
} from '@/services/scim.service';
import {
  AlertCircle,
  CheckCircle,
  Clock,
  Cloud,
  Edit2,
  History,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  TestTube2,
  Trash2,
  XCircle
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import usePermissionRouteAccess from '@/hooks/usePermissionRouteAccess';
import useHasPermission from '@/hooks/useHasPermission';
import { SCIM_CONFIGURATION_PERMISSION } from '@/constants/constant-permissions';
import DeleteConfirmationModal from '@/components/ui/DeleteConfirmationModal';
import Pagination from '@/components/ui/Pagination';
import SimpleSelect, { SelectOption } from '@/components/ui/SimpleSelect';
import { CardSkeleton, TableSkeleton } from '@/components/ui/SkeletonLoader';
import ScimConfigurationForm from '@/components/scim/ScimConfigurationForm';
import ScimSyncDetailModal from '@/components/scim/ScimSyncDetailModal';
import { SCIM_CONSTANTS, getProviderIcon, getProviderColor, getProviderLabel } from '@/constants/scim.constants';
import ConfirmationModal from '@/components/ui/ConfirmationModal';

// Provider type options for dropdowns
const PROVIDER_TYPE_OPTIONS: SelectOption[] = [
  { label: 'SCIM 2.0 (Standard)', value: 'SCIM' },
  { label: 'Skillmine Auth (CustomGroups)', value: 'SKILLMINE_AUTH' },
  { label: 'Spotlight', value: 'SPOTLIGHT' },
];

const FILTER_TYPE_OPTIONS: SelectOption[] = [
  { label: 'All Providers', value: 'all' },
  ...PROVIDER_TYPE_OPTIONS,
];

const ScimConfiguration: React.FC = () => {
  usePermissionRouteAccess([
    SCIM_CONFIGURATION_PERMISSION.ADMIN_SCIM_CONFIGURATION_READ,
    SCIM_CONFIGURATION_PERMISSION.ADMIN_SCIM_CONFIGURATION_WRITE,
  ]);
  const canWrite = useHasPermission([SCIM_CONFIGURATION_PERMISSION.ADMIN_SCIM_CONFIGURATION_WRITE]);
  const [activeTab, setActiveTab] = useState<'configurations' | 'sync-history'>('configurations');
  const [configurations, setConfigurations] = useState<ScimConfigurationDto[]>([]);
  const [syncLogs, setSyncLogs] = useState<ScimSyncLogDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(true);
  // Removed: isConnecting state (auto-connect during sync)
  const [isSyncing, setIsSyncing] = useState<{ configId: string; isDryRun: boolean } | null>(null);
  const [pollingTimeoutRef, setPollingTimeoutRef] = useState<NodeJS.Timeout | null>(null);

  // Stats data
  const [statsData, setStatsData] = useState({
    totalConfigs: 0,
    activeConfigs: 0,
    connectedConfigs: 0,
    totalSyncs: 0,
    successfulSyncs: 0,
    failedSyncs: 0,
    successRate: 0,
    last24Hours: 0
  });
  
  // Modal states
  const [showFormModal, setShowFormModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSyncConfirmModal, setShowSyncConfirmModal] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState<ScimConfigurationDto | null>(null);
  const [configToDelete, setConfigToDelete] = useState<{ id: string; name: string } | null>(null);
  const [syncToConfirm, setSyncToConfirm] = useState<{
    config: ScimConfigurationDto;
    syncType: 'full' | 'groups' | 'users';
    isDryRun: boolean;
  } | null>(null);
  const [selectedLog, setSelectedLog] = useState<ScimSyncLogDto | null>(null);
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');

  const [includeArchivedLogs, setIncludeArchivedLogs] = useState(false);

  // Pagination
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalConfigs, setTotalConfigs] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalLogs, setTotalLogs] = useState(0);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingTimeoutRef) {
        clearTimeout(pollingTimeoutRef);
      }
    };
  }, [pollingTimeoutRef]);

  // Load configurations and sync logs on mount
  useEffect(() => {
    const loadData = async () => {
      await Promise.all([
        loadConfigurations(),
        loadStatistics()
      ]);
    };
    loadData();

    // Auto-refresh statistics every minute (optional - keeps dashboard fresh)
    const statsInterval = setInterval(() => {
      loadStatistics();
    }, SCIM_CONSTANTS.REFRESH.STATS_INTERVAL_MS);

    return () => {
      clearInterval(statsInterval); // Cleanup on unmount
    };
  }, []);

  useEffect(() => {
    if (activeTab === 'configurations') {
      loadConfigurations();
    } else {
      loadSyncHistory();
    }
  }, [activeTab, page, limit, searchQuery, filterType, includeArchivedLogs]);

  const loadStatistics = async () => {
    try {
      setStatsLoading(true);
      const response = await scimService.getStatistics();
      if (response.success && response.data) {
        setStatsData({
          totalConfigs: response.data.configurations.total,
          activeConfigs: response.data.configurations.active,
          connectedConfigs: response.data.configurations.connected,
          totalSyncs: response.data.syncs.total,
          successfulSyncs: response.data.syncs.successful,
          failedSyncs: response.data.syncs.failed,
          successRate: response.data.syncs.successRate,
          last24Hours: response.data.syncs.last24Hours
        });
      }
    } catch (error) {
      console.error('Error loading statistics:', error);
      toast.error('Failed to load statistics');
    } finally {
      setStatsLoading(false);
    }
  };

  const loadConfigurations = async () => {
    try {
      setLoading(true);
      const response = await scimService.getConfigurations({
        page,
        limit,
        provider_type: filterType !== 'all' ? filterType : undefined,
        search: searchQuery || undefined
      });
      if (response.success && response.data) {
        setConfigurations(response.data || []);
        setTotalConfigs(response.meta_data?.total_records || 0);
        setTotalPages(Math.ceil((response.meta_data?.total_records || 0) / limit));
      }
    } catch (error) {
      console.error('Error loading configurations:', error);
      toast.error('Failed to load configurations');
    } finally {
      setLoading(false);
    }
  };

  const loadSyncHistory = async () => {
    try {
      setLoading(true);
      const response = await scimService.getSyncLogs({
        page,
        limit,
        include_archived: includeArchivedLogs
      });

      if (response.success && response.data) {
        const logs = response.data || [];
        setSyncLogs(logs);
        setTotalLogs(response.meta_data?.total_records || 0);
        return logs; // Return logs for polling mechanism
      }
      return [];
    } catch (error) {
      console.error('Error loading sync history:', error);
      toast.error('Failed to load sync history');
      return [];
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedConfig(null);
    setShowFormModal(true);
  };

  const handleEdit = (config: ScimConfigurationDto) => {
    setSelectedConfig(config);
    setShowFormModal(true);
  };

  const handleDelete = (config: ScimConfigurationDto) => {
    setConfigToDelete({ id: config._id || config.id || '', name: config.provider_name });
    setSelectedConfig(config);
    setShowDeleteModal(true);
  };

  const handleFormSubmit = async (data: CreateScimConfigurationDto | UpdateScimConfigurationDto) => {
    try {
      setIsFormSubmitting(true);
      if (selectedConfig) {
        // Update existing configuration
        await scimService.updateConfiguration(
          selectedConfig.id || selectedConfig._id || '',
          data as UpdateScimConfigurationDto
        );
        toast.success(`Configuration '${data.provider_name}' updated successfully`);
      } else {
        // Create new configuration
        await scimService.createConfiguration(data as CreateScimConfigurationDto);
        toast.success(`Configuration '${data.provider_name}' created successfully`);
      }
      setShowFormModal(false);
      setSelectedConfig(null);
      await Promise.all([loadConfigurations(), loadStatistics()]);
    } catch (error: any) {
      const contextualError = selectedConfig
        ? `Failed to update '${data.provider_name}': ${error.message}`
        : `Failed to create '${data.provider_name}': ${error.message}`;
      toast.error(contextualError);
      throw error; // Re-throw to let form handle it
    } finally {
      setIsFormSubmitting(false);
    }
  };

  const handleDeleteConfiguration = async () => {
    if (!selectedConfig) return;

    try {
      setLoading(true);
      await scimService.deleteConfiguration(selectedConfig.id || selectedConfig._id || '');
      toast.success(`Configuration '${selectedConfig.provider_name}' deleted successfully`);
      setShowDeleteModal(false);
      await Promise.all([loadConfigurations(), loadStatistics()]);
    } catch (error: any) {
      console.error('Failed to delete configuration:', error);
      const contextualError = `Failed to delete '${selectedConfig.provider_name}': ${error.message || 'Unknown error'}`;
      toast.error(contextualError);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (log: ScimSyncLogDto) => {
    try {
      const response = await scimService.getSyncLog(log.sync_id);
      if (response.success && response.data) {
        setSelectedLog(response.data);
      } else {
        toast.error('Failed to load sync details');
      }
    } catch (error: any) {
      console.error('Error loading sync details:', error);
      toast.error('Failed to load sync details');
    }
  };

  const handleRequestSync = (
    config: ScimConfigurationDto,
    syncType: 'full' | 'groups' | 'users',
    isDryRun: boolean = false
  ) => {
    // Show confirmation dialog for non-dry-run syncs
    if (!isDryRun) {
      setSyncToConfirm({ config, syncType, isDryRun });
      setShowSyncConfirmModal(true);
    } else {
      // Dry runs don't need confirmation
      handleRunSync(config, syncType, isDryRun);
    }
  };

  const handleConfirmSync = () => {
    if (syncToConfirm) {
      setShowSyncConfirmModal(false);
      handleRunSync(syncToConfirm.config, syncToConfirm.syncType, syncToConfirm.isDryRun);
      setSyncToConfirm(null);
    }
  };

  // Removed: handleConnect function (auto-connect during sync)

  const handleRunSync = async (
    config: ScimConfigurationDto,
    syncType: 'full' | 'groups' | 'users',
    isDryRun: boolean = false
  ) => {
    try {
      const configId = config._id || config.id || '';
      setIsSyncing({ configId, isDryRun });

      const request: ScimSyncRequestDto = {
        configuration_id: configId,
        sync_type: syncType,
        batchSize: config.sync_config?.batch_size || 100,
        dryRun: isDryRun
      };

      const response = await scimService.triggerSync(request);

      if (response.success && response.data) {
        const message = isDryRun
          ? `Dry run initiated! Syncing in background...`
          : `Sync ${syncType} initiated! Processing in background...`;
        toast.success(message);

        // Switch to history tab immediately
        if (isDryRun) {
          setActiveTab('sync-history');
        }

        // Start polling for status
        const syncId = (response.data as any).sync_id || (response.data as any).syncId;
        pollSyncStatus(syncId, configId, isDryRun);

      } else {
        toast.error(response.error?.error_description || 'Failed to initiate sync');
        setIsSyncing(null);
      }
    } catch (error: any) {
      console.error('Failed to initiate sync:', error);
      const errorMessage = error.message || 'An unexpected error occurred';
      const contextualError = `Failed to sync '${config.provider_name}': ${errorMessage}`;
      toast.error(contextualError);
      setIsSyncing(null);
    }
  };

  const pollSyncStatus = async (syncId: string, _configId: string, isDryRun: boolean) => {
    let pollCount = 0;

    const poll = async () => {
      try {
        pollCount++;

        // Reload sync history and get fresh logs (fixes stale closure bug)
        const freshLogs = await loadSyncHistory();

        // Find the sync log by syncId using fresh logs
        const syncLog = freshLogs.find(log => log.sync_id === syncId);

        if (syncLog) {
          const completedStatuses = [
            SCIM_CONSTANTS.SYNC_STATUS.COMPLETED,
            SCIM_CONSTANTS.SYNC_STATUS.SUCCESS,
            SCIM_CONSTANTS.SYNC_STATUS.FAILED,
            SCIM_CONSTANTS.SYNC_STATUS.ERROR,
            SCIM_CONSTANTS.SYNC_STATUS.PARTIAL
          ];
          const isComplete = completedStatuses.includes(syncLog.sync_status as any);

          if (isComplete) {
            // Sync completed - reload stats and configs once
            await Promise.all([
              loadStatistics(),
              loadConfigurations()
            ]);

            // Clear polling state
            setIsSyncing(null);
            setPollingTimeoutRef(null);

            const isSuccess = [
              SCIM_CONSTANTS.SYNC_STATUS.COMPLETED,
              SCIM_CONSTANTS.SYNC_STATUS.SUCCESS
            ].includes(syncLog.sync_status as any);

            if (isSuccess) {
              const message = isDryRun
                ? `Dry run completed! Check details for preview (no changes made)`
                : `Sync completed successfully! Check details for summary.`;
              toast.success(message);
            } else {
              toast.error(`Sync ${syncLog.sync_status.toLowerCase()}`);
            }
            return; // Stop polling
          }
        }

        // Continue polling if not complete and under max polls
        if (pollCount < SCIM_CONSTANTS.POLLING.MAX_POLLS) {
          const timeoutId = setTimeout(poll, SCIM_CONSTANTS.POLLING.INTERVAL_MS);
          setPollingTimeoutRef(timeoutId); // Track for cleanup
        } else {
          // Timeout - reload stats and configs once before stopping
          await Promise.all([
            loadStatistics(),
            loadConfigurations()
          ]);

          setIsSyncing(null);
          setPollingTimeoutRef(null);
          toast.error('Sync is taking longer than expected. Check sync history for status.');
        }

      } catch (error) {
        console.error('Error polling sync status:', error);
        toast.error('Error checking sync status. Please refresh the page.');
        setIsSyncing(null);
        setPollingTimeoutRef(null);
      }
    };

    // Start polling after initial delay
    const initialTimeoutId = setTimeout(poll, SCIM_CONSTANTS.POLLING.INITIAL_DELAY_MS);
    setPollingTimeoutRef(initialTimeoutId);
  };

  const getSyncStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED':
      case 'SUCCESS':
        return 'text-green-600 bg-green-100 border-green-200';
      case 'FAILED':
      case 'ERROR':
        return 'text-red-600 bg-red-100 border-red-200';
      case 'PARTIAL':
        return 'text-yellow-600 bg-yellow-100 border-yellow-200';
      case 'IN_PROGRESS':
        return 'text-blue-600 bg-blue-100 border-blue-200';
      default:
        return 'text-gray-600 bg-gray-100 border-gray-200';
    }
  };

  const getTimeSinceSync = (lastSyncDate: string) => {
    const now = new Date();
    const syncDate = new Date(lastSyncDate);
    const diffMs = now.getTime() - syncDate.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return syncDate.toLocaleDateString();
  };

  // Pagination handlers
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1);  // Reset to first page when changing limit
  };


  return (
    <div className="space-y-6">
      {/* Header with Gradient */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold mb-2 flex items-center gap-2">
              <Cloud className="w-7 h-7" />
              SCIM Configuration
            </h1>
            <p className="text-indigo-100">
              Manage user provisioning and synchronization with identity providers
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => canWrite && handleCreate()}
              disabled={!canWrite}
              title={!canWrite ? 'You do not have permission' : undefined}
              className={`flex items-center gap-2 px-4 py-2 bg-white text-indigo-600 rounded-lg hover:bg-indigo-50 transition-colors${!canWrite ? ' opacity-50 cursor-not-allowed' : ''}`}
            >
              <Plus className="w-5 h-5" />
              <span className="hidden sm:inline">Add Provider</span>
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-6">
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
            <p className="text-indigo-100 text-sm">Total Providers</p>
            {statsLoading ? (
              <div className="h-8 bg-white/20 animate-pulse rounded mt-1"></div>
            ) : (
              <>
                <p className="text-2xl font-bold">{statsData.totalConfigs}</p>
                <p className="text-xs text-indigo-200 mt-1">
                  {statsData.activeConfigs} active
                </p>
              </>
            )}
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
            <p className="text-indigo-100 text-sm">Connected</p>
            {statsLoading ? (
              <div className="h-8 bg-white/20 animate-pulse rounded mt-1"></div>
            ) : (
              <>
                <p className="text-2xl font-bold text-green-200">
                  {statsData.connectedConfigs}
                </p>
                <p className="text-xs text-indigo-200 mt-1">
                  {statsData.totalConfigs > 0
                    ? Math.round((statsData.connectedConfigs / statsData.totalConfigs) * 100)
                    : 0}% of total
                </p>
              </>
            )}
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
            <p className="text-indigo-100 text-sm">Total Syncs</p>
            {statsLoading ? (
              <div className="h-8 bg-white/20 animate-pulse rounded mt-1"></div>
            ) : (
              <>
                <p className="text-2xl font-bold">
                  {statsData.totalSyncs}
                </p>
                <p className="text-xs text-indigo-200 mt-1">
                  {statsData.last24Hours} in last 24h
                </p>
              </>
            )}
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
            <p className="text-indigo-100 text-sm">Success Rate</p>
            {statsLoading ? (
              <div className="h-8 bg-white/20 animate-pulse rounded mt-1"></div>
            ) : (
              <p className="text-2xl font-bold text-green-200">
                {statsData.successRate.toFixed(1)}%
              </p>
            )}
            <p className="text-xs text-indigo-200 mt-1">
              {statsData.successfulSyncs} succeeded
            </p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
            <p className="text-indigo-100 text-sm">Failed Syncs</p>
            {statsLoading ? (
              <div className="h-8 bg-white/20 animate-pulse rounded mt-1"></div>
            ) : (
              <>
                <p className="text-2xl font-bold text-red-200">
                  {statsData.failedSyncs}
                </p>
                <p className="text-xs text-indigo-200 mt-1">
                  {statsData.totalSyncs > 0
                    ? Math.round((statsData.failedSyncs / statsData.totalSyncs) * 100)
                    : 0}% failure rate
                </p>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="border-b">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('configurations')}
              className={`px-6 py-3 text-sm font-medium transition-colors ${
                activeTab === 'configurations'
                  ? 'border-b-2 border-indigo-600 text-indigo-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center gap-2">
                <Cloud className="w-4 h-4" />
                Providers
              </div>
            </button>
            <button
              onClick={() => {
                setActiveTab('sync-history');
                loadSyncHistory();
              }}
              className={`px-6 py-3 text-sm font-medium transition-colors ${
                activeTab === 'sync-history'
                  ? 'border-b-2 border-indigo-600 text-indigo-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center gap-2">
                <History className="w-4 h-4" />
                Sync History
              </div>
            </button>
          </nav>
        </div>
      </div>

      {/* Configurations Tab Content */}
      {activeTab === 'configurations' && (
        <>
          {/* Filters */}
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search providers..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setPage(1);  // Reset to page 1 on search
                  }}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div className="w-64">
                <SimpleSelect
                  value={filterType}
                  onChange={(value) => {
                    setFilterType(value as string);
                    setPage(1);  // Reset to page 1 on filter change
                  }}
                  options={FILTER_TYPE_OPTIONS}
                  placeholder="Filter by provider"
                  isSearchable={false}
                />
              </div>
              {/* TODO: Export functionality - will be implemented in next iteration */}
              {/* <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                <Download className="w-5 h-5" />
                Export
              </button> */}
            </div>
          </div>

          {/* Provider Cards Grid */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            {loading ? (
              // Skeleton loaders while loading
              <>
                {Array.from({ length: 4 }).map((_, index) => (
                  <CardSkeleton key={index} lines={6} />
                ))}
              </>
            ) : (
              configurations.map((config) => {
              const provider = getProviderColor(config.provider_type);
              const isConnected = config.is_connected ?? false;
              const hasNeverSynced = !config.last_sync;
              const lastSyncFailed = config.last_sync_status === 'FAILED' || config.last_sync_status === 'ERROR';

              return (
                <div key={config._id || config.id} className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-all duration-200">
                  {/* Card Header */}
                  <div className="p-6 border-b border-gray-100">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4 flex-1">
                        {/* Provider Icon */}
                        <div className={`w-14 h-14 ${provider} rounded-xl flex items-center justify-center text-white text-2xl shadow-sm`}>
                          {getProviderIcon(config.provider_type)}
                        </div>

                        {/* Provider Info */}
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-gray-900 mb-1">{config.provider_name}</h3>
                          <p className="text-sm text-gray-500 mb-2">{getProviderLabel(config.provider_type)}</p>

                          {/* Status Badges */}
                          <div className="flex flex-wrap items-center gap-2">
                            {/* Connection Status */}
                            {isConnected ? (
                              <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 bg-green-50 text-green-700 border border-green-200 rounded-full">
                                <CheckCircle className="w-3.5 h-3.5" />
                                Connected
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 bg-yellow-50 text-yellow-700 border border-yellow-200 rounded-full">
                                <AlertCircle className="w-3.5 h-3.5" />
                                Not Connected
                              </span>
                            )}

                            {/* Sync Status */}
                            {hasNeverSynced ? (
                              <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 bg-gray-50 text-gray-600 border border-gray-200 rounded-full">
                                <Clock className="w-3.5 h-3.5" />
                                Never Synced
                              </span>
                            ) : lastSyncFailed ? (
                              <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 bg-red-50 text-red-700 border border-red-200 rounded-full">
                                <XCircle className="w-3.5 h-3.5" />
                                Last Sync Failed
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 bg-blue-50 text-blue-700 border border-blue-200 rounded-full">
                                <CheckCircle className="w-3.5 h-3.5" />
                                Synced
                              </span>
                            )}

                            {/* Active Status */}
                            {config.is_active && (
                              <span className="text-xs font-medium px-2.5 py-1 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-full">
                                Active
                              </span>
                            )}

                            {/* Auto-Sync Schedule Status */}
                            {config.sync_config?.auto_sync_enabled && (
                              <span
                                className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 bg-purple-50 text-purple-700 border border-purple-200 rounded-full"
                                title={(() => {
                                  const freq = config.sync_config.sync_frequency;
                                  const predefined = ['*/30 * * * *', '0 * * * *', '0 */6 * * *', '0 */12 * * *', '0 2 * * *', '0 0 * * 0', '0 0 1 * *'];
                                  return !predefined.includes(freq || '') ? `Cron: ${freq}` : '';
                                })()}
                              >
                                <Clock className="w-3.5 h-3.5" />
                                Auto-sync: {(() => {
                                  const freq = config.sync_config.sync_frequency;
                                   if (freq === '*/30 * * * *')
                                     return 'Every 30m';
                                   if (freq === '0 * * * *') return 'Every 1h';
                                   if (freq === '0 */6 * * *')
                                     return 'Every 6h';
                                   if (freq === '0 */12 * * *')
                                     return 'Every 12h';
                                   if (freq === '0 2 * * *') return 'Daily';
                                   if (freq === '0 0 * * 0') return 'Weekly';
                                   if (freq === '0 0 1 * *') return 'Monthly';
                                  return `Custom (${freq})`;
                                })()}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => canWrite && handleEdit(config)}
                          disabled={!canWrite}
                          className={`p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors${!canWrite ? ' opacity-50 cursor-not-allowed' : ''}`}
                          title={!canWrite ? 'You do not have permission' : 'Edit configuration'}
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => canWrite && handleDelete(config)}
                          disabled={!canWrite}
                          className={`p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors${!canWrite ? ' opacity-50 cursor-not-allowed' : ''}`}
                          title={!canWrite ? 'You do not have permission' : 'Delete configuration'}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Card Body - Sync Information */}
                  <div className="p-6">
                    {/* Last Sync Info */}
                    {config.last_sync && (
                      <div className="mb-4 pb-4 border-b border-gray-100">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">Last synchronized</span>
                          <span className="font-medium text-gray-900">
                            {getTimeSinceSync(config.last_sync)}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {new Date(config.last_sync).toLocaleString()}
                        </div>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex items-center justify-between pt-4 border-t">
                      <div className="flex gap-2">
                        {/* Dry Run button - Test sync without making changes */}
                        <button
                          onClick={() => canWrite && handleRequestSync(config, 'full', true)}
                          disabled={!canWrite || isSyncing?.configId === (config._id || config.id)}
                          className="flex items-center gap-1 px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          title={!canWrite ? 'You do not have permission' : 'Test sync without making changes (preview only)'}
                        >
                          {(isSyncing?.configId === (config._id || config.id) && isSyncing?.isDryRun) ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <TestTube2 className="w-3 h-3" />
                          )}
                          Dry Run
                        </button>
                        <button
                          onClick={() => canWrite && handleRequestSync(config, 'full', false)}
                          disabled={!canWrite || isSyncing?.configId === (config._id || config.id)}
                          className="flex items-center gap-1 px-3 py-1 text-sm bg-green-100 text-green-700 rounded-lg hover:bg-green-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          title={!canWrite ? 'You do not have permission' : 'Sync data from provider to database (auto-connects if needed)'}
                        >
                          {(isSyncing?.configId === (config._id || config.id) && !isSyncing?.isDryRun) ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <RefreshCw className="w-3 h-3" />
                          )}
                          Sync Now
                        </button>
                      </div>
                      <div className="flex items-center gap-2">
                        {config.last_sync && (
                          <div className="flex items-center gap-1 text-xs text-gray-500" title={`Last synced: ${new Date(config.last_sync).toLocaleString()}`}>
                            <Clock className="w-3 h-3" />
                            {getTimeSinceSync(config.last_sync)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
            )}
          </div>

          {/* Pagination */}
          {configurations.length > 0 && (
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              totalItems={totalConfigs}
              itemsPerPage={limit}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleLimitChange}
              showItemsPerPage={true}
              itemsPerPageOptions={[10, 20, 50]}
            />
          )}

          {configurations.length === 0 && !loading && (
            <div className="bg-white rounded-lg shadow-sm p-12 text-center">
              <Cloud className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-2">No providers found</p>
              <p className="text-sm text-gray-500">
                {searchQuery || filterType !== 'all'
                  ? 'Try adjusting your filters'
                  : 'Create your first SCIM provider to get started'}
              </p>
            </div>
          )}
        </>
      )}

      {/* Sync History Tab Content */}
      {activeTab === 'sync-history' && (
        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-6">
            <div className="mb-4 flex items-start justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Sync History</h2>
                <p className="text-sm text-gray-600">View past synchronization activities and their status</p>
              </div>
              {/* Include archived toggle — same pattern as Audit Trail */}
              <label className="flex items-center gap-2 cursor-pointer select-none shrink-0">
                <input
                  type="checkbox"
                  checked={includeArchivedLogs}
                  onChange={(e) => { setIncludeArchivedLogs(e.target.checked); setPage(1); }}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">Include archived logs</span>
              </label>
            </div>

            {loading ? (
              <TableSkeleton rows={10} columns={6} showHeader={true} />
            ) : syncLogs.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Provider
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Sync Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Users
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Groups
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Duration
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Started At
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {syncLogs.map((log) => (
                      <tr key={log._id || log.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {log.provider_name}
                          {log.from_archive === true && (
                            <span className="ml-2 inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">
                              Archived
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <span className="flex items-center gap-1">
                            {log.sync_type}
                            {log.is_dry_run && (
                              <span className="text-xs px-1 py-0.5 bg-blue-100 text-blue-700 rounded">
                                Dry Run
                              </span>
                            )}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full border ${getSyncStatusColor(log.sync_status)}`}>
                            {log.sync_status === 'COMPLETED' || log.sync_status === 'SUCCESS' ? (
                              <CheckCircle className="w-3 h-3" />
                            ) : log.sync_status === 'FAILED' || log.sync_status === 'ERROR' ? (
                              <XCircle className="w-3 h-3" />
                            ) : log.sync_status === 'IN_PROGRESS' ? (
                              <RefreshCw className="w-3 h-3 animate-spin" />
                            ) : (
                              <AlertCircle className="w-3 h-3" />
                            )}
                            {log.sync_status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div className="flex flex-col">
                            <span>Total: {log.resources_synced?.users?.total || 0}</span>
                            <span className="text-xs text-gray-500">
                              ✅ {log.resources_synced?.users?.created || 0} created, 
                              {log.resources_synced?.users?.updated || 0} updated
                            </span>
                            {(log.resources_synced?.users?.failed || 0) > 0 && (
                              <span className="text-xs text-red-600">
                                ❌ {log.resources_synced?.users?.failed} failed
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div className="flex flex-col">
                            <span>Total: {log.resources_synced?.groups?.total || 0}</span>
                            <span className="text-xs text-gray-500">
                              ✅ {log.resources_synced?.groups?.created || 0} created, 
                              {log.resources_synced?.groups?.updated || 0} updated
                            </span>
                            {(log.resources_synced?.groups?.failed || 0) > 0 && (
                              <span className="text-xs text-red-600">
                                ❌ {log.resources_synced?.groups?.failed} failed
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {log.performance?.total_duration_ms 
                            ? `${(log.performance.total_duration_ms / 1000).toFixed(1)}s`
                            : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {new Date(log.started_at).toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <button
                            onClick={() => handleViewDetails(log)}
                            className="text-indigo-600 hover:text-indigo-900"
                          >
                            View Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8">
                <History className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No sync history available</p>
                <p className="text-sm text-gray-500 mt-1">
                  Sync history will appear here after your first synchronization
                </p>
              </div>
            )}

            {/* Pagination for Sync History */}
            {!loading && syncLogs.length > 0 && (
              <div className="mt-6">
                <Pagination
                  currentPage={page}
                  totalPages={Math.ceil(totalLogs / limit)}
                  onPageChange={handlePageChange}
                  totalItems={totalLogs}
                  itemsPerPage={limit}
                  onItemsPerPageChange={handleLimitChange}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* SCIM Configuration Form Modal */}
      <ScimConfigurationForm
        isOpen={showFormModal}
        onClose={() => {
          setShowFormModal(false);
          setSelectedConfig(null);
        }}
        onSubmit={handleFormSubmit}
        initialData={selectedConfig}
        isLoading={isFormSubmitting}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedConfig(null);
          setConfigToDelete(null);
        }}
        onConfirm={handleDeleteConfiguration}
        title="Delete Configuration"
        description="Are you sure you want to delete this SCIM configuration? This action cannot be undone."
        itemName={configToDelete?.name}
        isLoading={loading}
      />

      {/* Sync Confirmation Modal */}
      <ConfirmationModal
        isOpen={showSyncConfirmModal}
        onCancel={() => {
          setShowSyncConfirmModal(false);
          setSyncToConfirm(null);
        }}
        onConfirm={handleConfirmSync}
        title="Confirm Synchronization"
        message={`This will sync data from ${syncToConfirm?.config.provider_name} to your database. This operation may take several minutes and will ${syncToConfirm?.syncType === 'full' ? 'sync users, groups, and roles' : `sync ${syncToConfirm?.syncType} only`}.`}
        type="warning"
        confirmText="Sync Now"
        cancelText="Cancel"
      />

      {/* Sync Details Modal */}
      <ScimSyncDetailModal log={selectedLog} onClose={() => setSelectedLog(null)} />
    </div>
  );
};

export default ScimConfiguration;